'use client';

import { ReactElement, ReactNode, isValidElement, useMemo, useRef } from 'react';

import { Styles, css, sva } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { Token, token } from '@flipster/styled-system/tokens';

import { useDragScroll } from '../misc/browser/drag-scroll';
import { flattenFragment } from '../misc/node/fragment';
import { useControllableState } from '../misc/state/use-controllable-state';
import { StatusBadge } from './status-badge';

interface TabProps<T extends string> {
  value: T;
  label: ReactNode;
  redDot?: boolean;
  selectedColorSet?: {
    color?: Extract<Token, `colors.content.${string}`>;
    borderColor?: Extract<Token, `colors.${string}`>;
  };
  children?: ReactNode;
}

const Tab = <T extends string>({ children }: TabProps<T>) => {
  return children;
};

function isTabElement<T extends string>(element: ReactNode): element is ReactElement<TabProps<T>> {
  return isValidElement(element) && element.type === Tab;
}

export interface TabsProps<T extends string> {
  defaultValue?: T;
  value?: T;
  onChange?: (value: T) => void;
  onClick?: (value: T) => void;
  type: 'scrollable' | 'fixed' | 'responsive';
  size?: 'small' | 'medium' | 'large';
  border?: boolean;
  children: ReactNode;
  css?: Styles;
  tabsCss?: Styles;
  panelCss?: Styles;
  dragHandle?: boolean;
}

const TabsBase = <T extends string>({
  type,
  size = 'medium',
  border = true,
  defaultValue,
  value,
  onChange,
  onClick,
  css: cssProps,
  tabsCss,
  panelCss,
  dragHandle,
  children,
}: TabsProps<T>) => {
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const mouseEvents = useDragScroll(tabsRef);

  const [tabValue, setTabValue] = useControllableState<T>({
    defaultValue,
    value,
    onChange(v) {
      onChange?.(v);
      if (type === 'scrollable') {
        tabsRef.current
          ?.querySelector(`button[value="${v}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    },
  });

  const recipe = useMemo(() => tabsRecipe.raw({ type, size, border }), [type, size, border]);

  const tabList: ReactNode[] = [];
  let tabPanel: ReactNode = null;

  flattenFragment(children).forEach((child) => {
    if (isTabElement<T>(child)) {
      const isSelected = tabValue === child.props.value;
      tabList.push(
        <button
          type='button'
          role='tab'
          tabIndex={0}
          value={child.props.value}
          aria-selected={isSelected}
          onClick={() => {
            setTabValue(child.props.value);
            onClick?.(child.props.value);
          }}
          title={typeof child.props.label === 'string' ? child.props.label : undefined}
          className={css(recipe.tab)}
          key={child.props.value}
          style={
            {
              '--selected-color': token(child.props.selectedColorSet?.color ?? 'colors.content.default.level-5'),
              '--selected-border-color': token(
                child.props.selectedColorSet?.borderColor ?? 'colors.border.default.level-4',
              ),
            } as Record<string, string>
          }
        >
          <StatusBadge positionType='text' color='red' visible={Boolean(child.props.redDot)} css={recipe.statusBadge}>
            {child.props.label}
          </StatusBadge>
        </button>,
      );
      if (isSelected) tabPanel = child.props.children;
    }
  });

  return (
    <div className={css({ w: '100%' }, cssProps)}>
      <div className={css(recipe.tabsWrapper)} {...(dragHandle ? { 'data-drag-handle': '' } : {})}>
        <div
          role='tablist'
          className={css(recipe.tabs, tabsCss)}
          ref={tabsRef}
          {...(type === 'scrollable' ? mouseEvents : {})}
        >
          {tabList}
        </div>
      </div>
      <div className={css(panelCss)}>{tabPanel}</div>
    </div>
  );
};

type TabsComponent<T extends string> = {
  (props: TabsProps<T>): ReactElement | null;
  Tab: (props: TabProps<T>) => ReactElement | null;
};

const Tabs = Object.assign(TabsBase, { Tab });

TabsBase.displayName = 'Tabs';
Tab.displayName = 'Tabs.Tab';

export function createTabsComponent<T extends string>(): TabsComponent<T> {
  return Tabs as unknown as TabsComponent<T>;
}

const tabsRecipe = sva({
  slots: ['tabsWrapper', 'tabs', 'tab', 'panel', 'statusBadge'],
  base: {
    tabsWrapper: {
      w: '100%',
    },
    tabs: {
      ...flex.raw({ overflowX: 'auto' }),
    },
    tab: {
      ...center.raw({
        pos: 'relative',
        pt: 0,
        pb: 1,
        px: 9,
        textAlign: 'center',
        color: 'content.default.level-1',
        borderBottomWidth: '1px',
        borderBottomStyle: 'solid',
        borderBottomColor: 'transparent',
        _focusVisible: {
          '& > div': {
            outline: '2px solid',
            outlineColor: 'border.accent.info',
            rounded: 4,
          },
        },
        _selected: {
          color: 'var(--selected-color)',
          _after: {
            content: '""',
            pos: 'absolute',
            left: 0,
            right: 0,
            bottom: -1,
            h: 2,
            bgColor: 'var(--selected-color)',
          },
        },
        '&:not([aria-selected=true], [data-selected])': {
          _hover: {
            color: 'content.interaction.hover',
          },
        },
      }),
    },
    statusBadge: {
      display: 'inline-block',
      pl: 7,
      '@media (max-width: 360px)': {
        display: '-webkit-box',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        lineClamp: 2,
        boxOrient: 'vertical',
      },
    },
  },
  variants: {
    size: {
      large: {
        tab: {
          textStyle: 'Title/S',
          h: 48,
        },
      },
      medium: {
        tab: {
          textStyle: 'Text/L/Strong',
          h: 44,
        },
      },
      small: {
        tab: {
          textStyle: 'Text/M/Strong',
          h: 38,
        },
      },
    },
    type: {
      scrollable: {
        tab: {
          minW: 'max-content',
        },
      },
      fixed: {
        tab: {
          flex: 1,
          minW: 0,
        },
      },
      responsive: {
        tab: {
          tabletDown: {
            flex: 1,
            minW: 0,
          },
          tablet: {
            minW: 'max-content',
          },
        },
      },
    },
    border: {
      true: {
        tabsWrapper: {
          pos: 'relative',
          _before: {
            content: '""',
            pos: 'absolute',
            left: 0,
            bottom: 0,
            top: 'auto',
            bgColor: 'border.default.level-1',
            h: 1,
            w: '100%',
          },
        },
      },
    },
  },
});
